// excelRateCodes.js
import ExcelJS from 'exceljs'

// Enum: key → Excel label
export const CATEGORY_LABELS = {
  DirectfulCode: 'Directful',
  OTA: 'OTA',
  OTAOpaque: 'Opaque rates',
  GroupNoConvert: 'Group no convert',
  GroupNoReach: 'Group no reach',
  Employee: 'Employee',
  FriendsAndFamily: 'Friends & family',
  LongTermResident: 'Long term resident',
  AirlineDistress: 'Airline distress',
  Unknown: 'Uncategorized',
  CorporateLocal: 'Corporate local',
  CorporateBrand: 'Corporate brand',
  GovernmentLocal: 'Government local',
  GovernmentBrand: 'Government brand',
  Complimentary: 'Complimentary',
  RewardPointsClaim: 'Reward points claim',
  DiscountLocal: 'Discount local',
  DiscountBrand: 'Discount brand',
  DiscountClub: 'Discount club',
  Direct: 'Direct'
}

// Reverse lookup: Excel label → enum key
const LABEL_TO_KEY = Object.fromEntries(
  Object.entries(CATEGORY_LABELS).map(([key, label]) => [label, key])
)

export function rateCodesMatch(a, b) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * Merges imported rate codes into UI state.
 * Matches booking rate codes case-insensitively so Excel casing variants still categorize.
 */
export function mergeImportedRateCodes(rateState, importedRateCodes, isSubsidiaryLevel) {
  const updatedRateState = { ...rateState }
  const newChanges = []

  Object.keys(importedRateCodes).forEach(category => {
    if (category === 'Unknown') {
      return
    }

    const ratesToAdd = []

    importedRateCodes[category].forEach(rate => {
      const matchingUnknown = (updatedRateState.Unknown || []).filter(unknownRate =>
        rateCodesMatch(unknownRate.code, rate.code)
      )

      if (matchingUnknown.length > 0) {
        matchingUnknown.forEach(unknownRate => {
          if (!ratesToAdd.some(r => r.code === unknownRate.code)) {
            ratesToAdd.push({
              ...unknownRate,
              isImported: true
            })
          }
        })
        return
      }

      const allCategorized = Object.entries(updatedRateState)
        .filter(([key]) => key !== 'Unknown')
        .flatMap(([, rates]) => rates || [])

      if (!allCategorized.some(existingRate => rateCodesMatch(existingRate.code, rate.code))) {
        ratesToAdd.push({
          ...rate,
          isImported: true
        })
      }
    })

    if (ratesToAdd.length === 0) {
      return
    }

    const existingCategory = updatedRateState[category] || []
    const mergedCategory = [...existingCategory]

    ratesToAdd.forEach(newRate => {
      const existingIndex = mergedCategory.findIndex(
        existingRate => existingRate.code === newRate.code
      )

      if (existingIndex >= 0) {
        mergedCategory[existingIndex] = newRate
      } else {
        mergedCategory.push(newRate)
      }
    })

    updatedRateState[category] = mergedCategory

    if (updatedRateState.Unknown) {
      updatedRateState.Unknown = updatedRateState.Unknown.filter(
        unknownRate => !ratesToAdd.some(r => rateCodesMatch(r.code, unknownRate.code))
      )
    }

    ratesToAdd.forEach(rate => {
      newChanges.push({
        isSubsidiaryLevel,
        code: rate.code,
        kind: category
      })
    })
  })

  return { updatedRateState, newChanges }
}

/**
 * Validates an Excel file for rate code import.
 * Checks file structure, required columns, and category values.
 *
 * @param {File} file - The Excel file to validate
 * @returns {Promise<{valid: boolean, errors: string[], warnings: string[], rowCount: number}>}
 */
export async function validateExcelFile(file) {
  const errors = []
  const warnings = []
  let rowCount = 0

  // Check file extension
  const fileName = file.name || ''
  const validExtensions = ['.xlsx', '.xls']
  const hasValidExtension = validExtensions.some(ext =>
    fileName.toLowerCase().endsWith(ext)
  )

  if (!hasValidExtension) {
    errors.push(
      `Invalid file type. Expected Excel file (.xlsx or .xls), got: ${fileName}`
    )
    return { valid: false, errors, warnings, rowCount }
  }

  try {
    const arrayBuffer = await file.arrayBuffer()
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(arrayBuffer)

    // Check for worksheets
    if (!workbook.worksheets || workbook.worksheets.length === 0) {
      errors.push('Excel file contains no worksheets')
      return { valid: false, errors, warnings, rowCount }
    }

    const sheet = workbook.getWorksheet('Rates') || workbook.worksheets[0] || null

    if (!sheet) {
      errors.push('Could not find any sheet in Excel file')
      return { valid: false, errors, warnings, rowCount }
    }

    // Check if 'Rates' sheet exists (warning if using fallback)
    if (!workbook.getWorksheet('Rates')) {
      warnings.push(`Sheet named "Rates" not found. Using first sheet: "${sheet.name}"`)
    }

    // Validate header row
    const headerRow = sheet.getRow(1)
    const codeHeader = headerRow.getCell(1).value
    const categoryHeader = headerRow.getCell(2).value

    if (!codeHeader && !categoryHeader) {
      errors.push('Header row appears to be empty. Expected columns: Code, Category')
    }

    // Validate data rows
    const invalidCategories = new Set()
    const emptyCodeRows = []

    sheet.eachRow((row, rowNo) => {
      if (rowNo === 1) return // skip header

      rowCount++

      const codeValue = row.getCell(1).value
      const categoryValue = row.getCell(2).value

      // Check for empty code
      if (!codeValue || String(codeValue).trim() === '') {
        emptyCodeRows.push(rowNo)
      }

      // Validate category value
      if (categoryValue) {
        const categoryLabel = String(categoryValue).trim()
        const isValidCategory = Object.values(CATEGORY_LABELS).includes(categoryLabel)

        if (!isValidCategory) {
          invalidCategories.add(categoryLabel)
        }
      }
    })

    // Report empty code rows
    if (emptyCodeRows.length > 0) {
      if (emptyCodeRows.length <= 5) {
        warnings.push(`Empty code values found in rows: ${emptyCodeRows.join(', ')}`)
      } else {
        warnings.push(
          `Empty code values found in ${
            emptyCodeRows.length
          } rows (first 5: ${emptyCodeRows.slice(0, 5).join(', ')}...)`
        )
      }
    }

    // Report invalid categories as warnings (not errors) - they will be filtered out
    if (invalidCategories.size > 0) {
      const invalidList = Array.from(invalidCategories).slice(0, 5).join(', ')
      const suffix = invalidCategories.size > 5 ? '...' : ''
      warnings.push(
        `Unknown category values found: ${invalidList}${suffix}. These rows will be available for download.`
      )
    }

    // Check for minimum data
    if (rowCount === 0) {
      errors.push('Excel file contains no data rows (only header found)')
    }
  } catch (error) {
    errors.push(`Failed to parse Excel file: ${error.message}`)
    return { valid: false, errors, warnings, rowCount }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    rowCount
  }
}

/**
 * Reads uploaded Excel file and returns grouped bucket JSON.
 * Output:
 * {
 *   buckets: { OTA: [{code}], ... },
 *   unknownRows: [{ code, category, rowNumber }]
 * }
 */
export async function parseExcelToRateCodeBuckets(file) {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)

  const sheet = workbook.getWorksheet('Rates') || workbook.worksheets[0] || null

  if (!sheet) throw new Error('Could not find any sheet in Excel')

  // Initialize empty buckets for all categories (so missing ones remain like [])
  const result = {}
  const unknownRows = []

  // eslint-disable-next-line no-unused-vars
  const { Unknown, ...categoryLabelsExceptUnknown } = CATEGORY_LABELS

  for (const key of Object.keys(categoryLabelsExceptUnknown)) {
    result[key] = []
  }

  // Last row wins for duplicate codes (case-insensitive), matching backend save behavior.
  const codeAssignments = new Map()

  sheet.eachRow((row, rowNo) => {
    if (rowNo === 1) return // skip header

    const codeValue = row.getCell(1).value
    const categoryValue = row.getCell(2).value

    if (categoryValue === CATEGORY_LABELS.Unknown) return // skip Uncategorized rows

    if (!codeValue) return

    const code = String(codeValue).trim()
    const categoryLabel = categoryValue ? String(categoryValue).trim() : ''

    // map label to enum key
    const bucketKey = LABEL_TO_KEY[categoryLabel]

    if (!bucketKey) {
      // Unknown category - collect for CSV download
      unknownRows.push({
        code,
        category: categoryLabel || '(empty)',
        rowNumber: rowNo
      })
      return
    }

    codeAssignments.set(code.toLowerCase(), { bucketKey, code })
  })

  codeAssignments.forEach(({ bucketKey, code }) => {
    result[bucketKey].push({ code })
  })

  return { buckets: result, unknownRows }
}

/**
 * Generates a CSV string from unknown rows
 * @param {Array<{code: string, category: string, rowNumber: number}>} unknownRows
 * @returns {string} CSV content
 */
export function generateUnknownRowsCsv(unknownRows) {
  const header = 'Code,Category,Original Row Number'
  const rows = unknownRows.map(row => `"${row.code}","${row.category}",${row.rowNumber}`)
  return [header, ...rows].join('\n')
}

/**
 * Creates a downloadable CSV blob URL from unknown rows
 * @param {Array<{code: string, category: string, rowNumber: number}>} unknownRows
 * @returns {string} Blob URL for download
 */
export function createUnknownRowsCsvDownloadUrl(unknownRows) {
  const csvContent = generateUnknownRowsCsv(unknownRows)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  return URL.createObjectURL(blob)
}

export default parseExcelToRateCodeBuckets
